import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { TruckModel } from "../../domain/models/truckModel";
import { GraphQLErrorMessage } from "../../../../../shared/gql-client";
import { DealerRequestModel } from "../../domain/models/dealerModel";
import AddRequestToRoute from "../actions/AddRequestToRoute";
import AddDealerToRoute from "../actions/AddDealerToRoute";
import MoveSlipToRoute from "../actions/MoveSlipToRoute";
import MoveRequestToRoute from "../actions/MoveRequestToRoute";
import RemoveRequestFromRoute from "../actions/RemoveRequestFromRoute";
import RemoveSlipFromRoute from "../actions/RemoveSlipFromRoute";
import { RouteModel } from "../../domain/models/routeModel";
import {
  getRequestsFromDay,
  setRoutesDefinitive,
  updateLayoutForRoute,
} from "./PlanningThunks";
import i18next from "i18next";
import { extraReducersGetPlanningRoutes } from "./thunks/route/getPlanningRoutesThunk";
import { extraReducersGetPlanningTrucks } from "./thunks/truck/getPlanningTrucksThunk";
import { extraReducersCreatePlanningRoute } from "./thunks/route/createPlanningRouteThunk";
import { extraReducersUpdatePlanningRouteTruck } from "./thunks/route/updatePlanningRouteTruckThunk";
import { planningDragEnd } from "./thunks/planningDragEndThunk";
import { updatePlanningPackingSlipPickupAmount } from "./thunks/packingSlip/updatePlanningPackingSlipPickupAmount";

export interface PlanningSliceModel {
  loading: boolean;
  routes: RouteModel[];
  trucks: TruckModel[];
  dealers: DealerRequestModel[];
  date: string;
  requestsDate: string;
  draggableId: string | null;
  selectedRoute: RouteModel | undefined;
}

const initialState = {
  loading: true,
  routes: [],
  trucks: [],
  dealers: [],
  date: new Date().toISOString().split("T")[0],
  requestsDate: new Date().toISOString().split("T")[0],
  draggableId: null,
  selectedRoute: undefined,
} as PlanningSliceModel;

const planningSlice = createSlice({
  name: "planning",
  initialState,
  reducers: {
    setDraggableId: (state, action) => {
      state.draggableId = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setDate: (state, action) => {
      state.date = action.payload;
      state.requestsDate = action.payload;
    },
    setRequestsDate: (state, action) => {
      state.requestsDate = action.payload;
    },
    setSelectedRoute: (state, action) => {
      state.selectedRoute = action.payload;
    },
    changeDriverForRoute: (state, action) => {
      const route: RouteModel = action.payload.route;
      const id: string = action.payload.id;
      const label: string = action.payload.label;

      state.routes = state.routes.map((r) => {
        if (r.id === route.id) {
          return { ...r, driver: { id: id, name: label } };
        }
        return r;
      });
    },
    unsetRouteCustom: (state, action) => {
      const route: RouteModel = action.payload.route;
      const custom = action.payload.custom;
      state.routes = state.routes.map((r) => {
        if (r.id === route.id) {
          return { ...r, custom: custom };
        }
        return r;
      });
    },
    removeRoute: (state, action) => {
      const route: RouteModel = action.payload;

      // Add all the packing slips back to dealers.
      route.packingSlips.forEach((packingSlip) => {
        const existingDealer = state.dealers.find(
          (d) => d.relation.id === packingSlip.relation.id,
        );

        if (existingDealer) {
          existingDealer.deliver_requests.push(...packingSlip.requests);
        } else {
          state.dealers.push({
            relation: packingSlip.relation,
            deliver_requests: packingSlip.requests,
            pickup_amount: packingSlip.pickup_amount,
          });
        }
      });

      // Remove the route.
      state.routes = state.routes.filter((r) => r.id !== route.id);
    },
    moveRequestToRoute: (state, action) => {
      state.routes = MoveRequestToRoute(state.routes, action.payload);
    },
    moveSlipToRoute: (state, action) => {
      state.routes = MoveSlipToRoute(state.routes, action.payload);
    },
    addRequestToRoute: (state, action) => {
      const { routes, dealers } = AddRequestToRoute(
        state.routes,
        state.dealers,
        action.payload,
      );

      state.routes = routes;
      state.dealers = dealers;
    },
    addDealerToRoute: (state, action) => {
      const { routes, dealers } = AddDealerToRoute(
        state.routes,
        state.dealers,
        action.payload,
      );

      state.routes = routes;
      state.dealers = dealers;
    },
    removeRequestFromRoute: (state, action) => {
      const { routes, dealers } = RemoveRequestFromRoute(
        state.routes,
        state.dealers,
        action.payload,
      );

      state.routes = routes;
      state.dealers = dealers;
    },
    removeSlipFromRoute: (state, action) => {
      const { routes, dealers } = RemoveSlipFromRoute(
        state.routes,
        state.dealers,
        action.payload,
      );

      state.routes = routes;
      state.dealers = dealers;
    },
    changePickupAmount: (state, action) => {
      state.routes = state.routes.map((route) => {
        return {
          ...route,
          packingSlips: route.packingSlips.map((packingSlip) =>
            packingSlip.id === action.payload.packingSlip.id
              ? { ...packingSlip, pickup_amount: action.payload.newAmount }
              : packingSlip,
          ),
        };
      });
    },
    changeRouteTruck: (
      state,
      action: PayloadAction<{ routeId: string; truck: TruckModel }>,
    ) => {
      state.routes = state.routes.map((route) => {
        if (route.id === action.payload.routeId) {
          return {
            ...route,
            truck: action.payload.truck,
          };
        }
        return route;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRequestsFromDay.fulfilled, (state, action) => {
      state.dealers = action.payload;
    });
    builder.addCase(updateLayoutForRoute.fulfilled, (state, action) => {
      state.routes = state.routes.map((route) => {
        return route.id === (action.payload as RouteModel).id
          ? {
              ...route,
              layout: (action.payload as RouteModel).layout,
            }
          : route;
      });
    });
    builder.addCase(planningDragEnd.fulfilled, (state, action) => {
      const response = action.payload as RouteModel;
      if (response == null) return;

      state.routes = state.routes.map((route) => {
        if (route.id === response.id) {
          return response;
        }
        return route;
      });
    });
    extraReducersGetPlanningRoutes(builder);
    extraReducersGetPlanningTrucks(builder);
    extraReducersCreatePlanningRoute(builder);
    extraReducersUpdatePlanningRouteTruck(builder);
    builder.addMatcher(
      (action) =>
        [
          planningDragEnd.rejected.type,
          updateLayoutForRoute.rejected.type,
          updatePlanningPackingSlipPickupAmount.rejected.type,
          setRoutesDefinitive.rejected.type,
        ].includes(action.type),
      (state, action: { payload: GraphQLErrorMessage }) => {
        state.loading = false;
        console.error(action.payload);
        toast.error(i18next?.t("common:" + action.payload.message));
      },
    );
  },
});

export const {
  addRequestToRoute,
  addDealerToRoute,
  moveSlipToRoute,
  moveRequestToRoute,
  setLoading,
  setDate,
  setDraggableId,
  removeRequestFromRoute,
  removeSlipFromRoute,
  setSelectedRoute,
  removeRoute,
  changeDriverForRoute,
  setRequestsDate,
  changePickupAmount,
  changeRouteTruck,
  unsetRouteCustom,
} = planningSlice.actions;

export default planningSlice.reducer;
